/**
 * Multipliers for the simple Keynesian cross and for deposit expansion.
 *
 * The fiscal formulas assume a closed economy with lump-sum taxes: no income tax rate,
 * no imports, no crowding out, prices fixed and spare capacity — the model OpenStax
 * Principles of Macroeconomics and most textbooks teach first. The money-multiplier
 * formulas are the textbook reserve-ratio model, with optional excess reserves and a
 * cash-to-deposit ratio.
 */

export interface FiscalMultipliers {
  mpc: number;
  mps: number;
  /** ΔY ÷ ΔG = 1 ÷ (1 − MPC) = 1 ÷ MPS. */
  spending: number;
  /** ΔY ÷ ΔT = −MPC ÷ (1 − MPC) for a lump-sum tax change. */
  tax: number;
  /** ΔY ÷ ΔTR = MPC ÷ (1 − MPC) for a lump-sum transfer change. */
  transfer: number;
  /** Equal ΔG and ΔT: spending + tax multiplier, always 1. */
  balancedBudget: number;
}

/** Why the multipliers cannot be found, or the multipliers themselves. */
export type FiscalResult = FiscalMultipliers | "out-of-range" | "unbounded";

/** Multipliers from the marginal propensity to consume (0 ≤ MPC < 1). */
export function fiscalMultipliers(mpc: number): FiscalResult {
  if (!Number.isFinite(mpc) || mpc < 0 || mpc > 1) {
    return "out-of-range";
  }
  if (mpc === 1) {
    return "unbounded";
  }
  const mps = 1 - mpc;
  const spending = 1 / mps;
  const tax = -mpc / mps;
  return {
    mpc,
    mps,
    spending,
    tax,
    transfer: -tax,
    balancedBudget: spending + tax,
  };
}

export interface FiscalPolicy {
  spendingChange: number;
  /** Change in lump-sum taxes: positive for a tax rise, negative for a cut. */
  taxChange: number;
}

export interface FiscalEffect {
  fromSpending: number;
  fromTax: number;
  total: number;
  /** The first-round change in spending: ΔG − MPC × ΔT. */
  firstRound: number;
}

export function fiscalEffect(
  m: FiscalMultipliers,
  policy: FiscalPolicy
): FiscalEffect {
  const fromSpending = m.spending * policy.spendingChange;
  const fromTax = m.tax * policy.taxChange;
  return {
    fromSpending,
    fromTax,
    total: fromSpending + fromTax,
    firstRound: policy.spendingChange - m.mpc * policy.taxChange,
  };
}

export interface RoundRow {
  round: number;
  /** The new spending (or new deposits) in this round. */
  amount: number;
  /** The running total after this round. */
  cumulative: number;
  /** Share of the full effect reached so far, 0–1. */
  share: number;
}

/**
 * The geometric chain: each round passes a fraction `keep` of the last round on.
 * `first` is the round-1 amount and `limit` is where the chain ends: first ÷ (1 − keep).
 */
export function roundTable(
  first: number,
  keep: number,
  rounds: number
): RoundRow[] {
  const limit = keep < 1 ? first / (1 - keep) : Number.NaN;
  const rows: RoundRow[] = [];
  let amount = first;
  let cumulative = 0;
  for (let round = 1; round <= rounds; round++) {
    cumulative += amount;
    rows.push({
      round,
      amount,
      cumulative,
      share: limit === 0 ? 1 : cumulative / limit,
    });
    amount *= keep;
  }
  return rows;
}

export interface MoneyInput {
  /** Required reserve ratio as a decimal. */
  reserveRatio: number;
  /** Excess reserves ÷ deposits, as a decimal. */
  excessRatio: number;
  /** Currency held by the public ÷ deposits, as a decimal. */
  cashRatio: number;
  /** The increase in the monetary base (new reserves). */
  newBase: number;
}

export interface MoneyResult {
  /** 1 ÷ (rr + e + c): total deposits per unit of new base. */
  depositMultiplier: number;
  /** (1 + c) ÷ (rr + e + c): the money supply per unit of base. */
  moneyMultiplier: number;
  /** The simple textbook multiplier, 1 ÷ rr. */
  simple: number;
  depositsCreated: number;
  moneySupplyChange: number;
  /** New lending that deposit creation supports: deposits created − new base. */
  newLending: number;
}

export type MoneyOutcome = MoneyResult | "invalid-ratio" | "no-leakage";

/** The deposit and money multipliers; null input ratios are checked by the caller. */
export function moneyMultiplier(input: MoneyInput): MoneyOutcome {
  const { reserveRatio: rr, excessRatio: e, cashRatio: c, newBase } = input;
  if (
    ![rr, e, c, newBase].every(Number.isFinite) ||
    rr < 0 ||
    e < 0 ||
    c < 0 ||
    rr + e > 1
  ) {
    return "invalid-ratio";
  }
  const leakage = rr + e + c;
  if (leakage <= 0) {
    return "no-leakage";
  }
  const depositMultiplier = 1 / leakage;
  const depositsCreated = newBase * depositMultiplier;
  return {
    depositMultiplier,
    moneyMultiplier: (1 + c) / leakage,
    simple: rr > 0 ? 1 / rr : Number.NaN,
    depositsCreated,
    moneySupplyChange: newBase * ((1 + c) / leakage),
    newLending: depositsCreated - newBase,
  };
}
